import {HTTP} from "../http";
import Report from "../model/Report";
import WarningOption from "../model/WarningOption";
import ReportUpdate from "../model/ReportUpdate";

class ReportService {

  basePath = 'api/v2/reports';

  constructor() {
  }

  /**
   *
   * @param title
   * @param text
   * @param categoryId
   * @param customerIds
   * @returns {Promise<Report>}
   */
  create(title, text, categoryId, customerIds, maintenanceStart, maintenanceEnd) {
    const data = {
      title: title,
      text: text,
      categoryId: categoryId,
      customerIds: customerIds,
      maintenanceStart: maintenanceStart,
      maintenanceEnd: maintenanceEnd
    };
    return HTTP.post(this.basePath, data).then((response) => {
      return this._reportFromJson(response.data);
    })
  }

  /**
   * @param reportId
   * @param update
   * @returns {Promise<Report>>}
   */
  createUpdate(reportId, update) {
    return new Promise((resolve) => {
      HTTP.post(`${this.basePath}/${reportId}/updates`, update)
        .then(response => {
          const updatedReport = this._reportFromJson(response.data);
          resolve(updatedReport);
        });
    })
  }

  /**
   *
   * @param {Object} params - Optional parameters for filtering reports
   * @param {string} params.tenantId - Optional tenant ID for superadmins
   * @returns {Promise<Array>}
   */
  get(params) {
    return HTTP.get(this.basePath, { params }).then((response) => {
      const reports = [];
      response.data.forEach(report => {
        reports.push(this._reportFromJson(report))
      });
      return reports;
    })
  }

  /**
   *
   * @param reportId
   * @returns {Promise<Report>}
   */
  getOne(reportId) {
    return HTTP.get(`${this.basePath}/${reportId}`).then((response) => {
      return this._reportFromJson(response.data);
    });
  }

  /**
   *
   * @param report
   * @returns {Promise<Report>}
   */
  patch(report, finalMessage) {
    const data = {
      title: report.subject,
      text: report.content,
      categoryId: report.warningOption.value,
      customerIds: report.customers.map(customer => customer.id),
      maintenanceStart: report.maintenanceStart,
      maintenanceEnd: report.maintenanceEnd,
      finishReportText: finalMessage,
    };
    return HTTP.patch(`${this.basePath}/${report.id}`, data).then((result) => {
      const report = result.data;
      return this._reportFromJson(report);
    });
  }

  /**
   *
   * @param reportId
   * @param update
   * @returns {Promise<ReportUpdate>}
   */
  patchUpdate(reportId, update) {
    return new Promise((resolve => {
      HTTP.patch(`${this.basePath}/${reportId}/updates/${update.id}`, update)
        .then(response => {
          const updatedReport = this._reportUpdateFromJson(response.data);
          resolve(updatedReport);
        });
    }));

  }

  /**
   *
   * @param id
   * @returns {AxiosPromise}
   */
  delete(id) {
    return HTTP.delete(`${this.basePath}/${id}`)
  }

  /**
   *
   * @param json {any}
   * @returns {Report}
   * @private
   */
  _reportFromJson(json) {
    let category = new WarningOption(
      json.category.id, json.category.title, json.category.color, json.category.level, json.category.type);

    let updates = json.updates
      .map(update => new ReportUpdate(
        update.id, update.text, update.createdBy, update.createdDate,
        update.updatedBy, update.updatedDate, update.version, update.createdByAccount, update.updatedByAccount, update.createdByName,
        update.createdByEmail,
        update.updatedByName,
        update.updatedByEmail,
      ));

    return new Report(
      json.id,
      json.rawTitle,
      json.text,
      category,
      json.customers,
      json.createdDate,
      json.updatedDate,
      //TODO: get user by login name
      json.createdByAccount,
      json.createdBy,
      json.updatedByAccount,
      json.updatedBy,
      json.maintenanceStart,
      json.maintenanceEnd,
      updates,
      json.textUpdatedByAccount,
      json.textUpdatedBy,
      json.textUpdatedDate,
      json.createdByName,
      json.createdByEmail,
      json.updatedByName,
      json.updatedByEmail,
      json.textUpdatedByName,
      json.textUpdatedByEmail
    );
  }

  /**
   *
   * @param json
   * @returns {ReportUpdate}
   * @private
   */
  _reportUpdateFromJson(json) {
    return new ReportUpdate(
      json.id,
      json.text,
      json.createdBy,
      json.createdDate,
      json.updatedBy,
      json.updatedDate,
      json.version,
      json.createdByAccount,
      json.updatedByAccount
    )
  }
}

/**
 *
 * @type {ReportService}
 */
export const reportService = new ReportService();
